import { useState, useCallback, useEffect } from 'react';
import { ToggleProps } from '@/shared/types/toggle';

interface UseToggleReturn {
  isChecked: boolean;
  toggle: () => void;
  setChecked: (value: boolean) => void;
  isControlled: boolean;
}

export const useToggle = (props: ToggleProps): UseToggleReturn => {
  const { checked, defaultChecked = false, onChange, disabled } = props;

  // Determinar si es controlado
  const isControlled = checked !== undefined;

  // Estado interno para modo no controlado
  const [internalChecked, setInternalChecked] = useState(defaultChecked);

  // Sincronizar estado interno con prop controlada
  useEffect(() => {
    if (isControlled && checked !== undefined) {
      setInternalChecked(checked);
    }
  }, [isControlled, checked]);

  const isChecked = isControlled ? (checked ?? false) : internalChecked;

  const toggle = useCallback(() => {
    if (disabled) return;

    const newValue = !isChecked;

    if (!isControlled) {
      setInternalChecked(newValue);
    }

    onChange?.(newValue);
  }, [isChecked, isControlled, disabled, onChange]);

  const setChecked = useCallback(
    (value: boolean) => {
      if (disabled) return;

      if (!isControlled) {
        setInternalChecked(value);
      }

      onChange?.(value);
    },
    [isControlled, disabled, onChange]
  );

  return {
    isChecked,
    toggle,
    setChecked,
    isControlled,
  };
};
